import { useState, useEffect } from "react";
import ModalSheet from "./ModalSheet";
import { COLOR, FONT, RADIUS, SPACE } from "../lib/tokens";

// TechniqueSheet — the "Explain this technique →" surface.
//
// Bottom sheet that renders one row from the techniques catalog.
// Sections:
//
//   - kicker:        category tag (CATEGORY-COLORED uppercase)
//   - title:         technique name (FONT.itemTitle italic, big)
//   - short_desc:    one-liner subtitle (FONT.forma italic)
//   - long_desc:     full prose explanation
//   - parameters:    duration / heat / temperature grid
//   - doneness:      "Look for" bullets — sensory cues the cook verifies
//   - pitfalls:      "Watch out for" bullets — common failure modes
//   - related:       tappable chips that swap the sheet to a sibling row
//
// Caller passes the FULL catalog (from useTechniques()) and an initial
// slug. The sheet manages its own "current technique" state so taps on
// the related-techniques chips swap content in-place without remount.

const CATEGORY_COLOR = {
  mixing:    COLOR.sky,
  heating:   COLOR.rose,
  doughs:    COLOR.gold,
  prep:      COLOR.leaf,
  finishing: COLOR.plum,
};

const CATEGORY_LABEL = {
  mixing:    "MIXING",
  heating:   "HEATING",
  doughs:    "DOUGHS",
  prep:      "PREP",
  finishing: "FINISHING",
};

function formatDuration(parameters) {
  if (!parameters) return null;
  const min = parameters.duration_min;
  const max = parameters.duration_max;
  if (min == null && max == null) return null;
  if (min == null || max == null || min === max) {
    const v = max ?? min;
    return v < 1 ? `${Math.round(v * 60)} sec` : `${v} min`;
  }
  if (min < 1 && max < 1) return `${Math.round(min * 60)}–${Math.round(max * 60)} sec`;
  return `${min}–${max} min`;
}

function formatHeat(parameters) {
  if (!parameters?.heat) return null;
  const map = {
    "low":         "Low",
    "medium-low":  "Medium-low",
    "medium":      "Medium",
    "medium-high": "Medium-high",
    "high":        "High",
    "warm":        "Warm",
  };
  return map[parameters.heat] || parameters.heat;
}

function formatTemperature(parameters) {
  if (!parameters) return null;
  if (parameters.temperature_f) return `${parameters.temperature_f}°F`;
  if (parameters.internal_temp_f) return `Internal ${parameters.internal_temp_f}°F`;
  return null;
}

export default function TechniqueSheet({ slug, catalog, bySlug, onClose }) {
  const [activeSlug, setActiveSlug] = useState(slug);

  // When the caller passes a new starting slug (e.g. user taps a
  // different chip in CookMode while the sheet is already mounted),
  // sync state. Without this, the sheet shows whatever the first
  // technique was and ignores subsequent reopens.
  useEffect(() => { setActiveSlug(slug); }, [slug]);

  const technique = bySlug?.get(activeSlug) || null;

  // Defensive — if the slug isn't in the catalog, render an empty
  // sheet with just the close button so the cook isn't stranded with
  // a frozen tap target. This happens if a slug is mentioned in
  // aliases but the row was deleted from the catalog after the page
  // loaded with the cached version.
  if (!technique) {
    return (
      <ModalSheet onClose={onClose} label="EXPLAIN">
        <div style={{
          padding: "24px 8px", textAlign: "center",
          fontFamily: FONT.forma, fontSize: 14, color: COLOR.muted,
        }}>
          Technique not found in catalog.
        </div>
      </ModalSheet>
    );
  }

  const categoryColor = CATEGORY_COLOR[technique.category] || COLOR.gold;
  const categoryLabel = CATEGORY_LABEL[technique.category] || technique.category.toUpperCase();
  const duration  = formatDuration(technique.parameters);
  const heat      = formatHeat(technique.parameters);
  const temp      = formatTemperature(technique.parameters);
  const hasParams = duration || heat || temp;

  return (
    <ModalSheet onClose={onClose}>
      {/* Category kicker — colored chip up top, replaces ModalSheet's
          default label slot so we can color it per category. */}
      <div style={{
        display: "inline-block", alignSelf: "flex-start",
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        color: categoryColor, letterSpacing: "0.14em",
        paddingTop: 4, marginBottom: 10,
      }}>
        {categoryLabel}
      </div>

      {/* Title — italic editorial New Kansas. */}
      <h2 style={{
        fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
        fontSize: 32, lineHeight: 1.05, color: COLOR.ink,
        letterSpacing: "-0.01em", margin: "0 0 10px",
      }}>
        {technique.name}
      </h2>

      {/* Short description — one-liner subtitle in Forma italic. */}
      <p style={{
        fontFamily: FONT.forma, fontStyle: "italic", fontWeight: 500,
        fontSize: 15, lineHeight: 1.45, color: COLOR.muted,
        margin: "0 0 22px",
      }}>
        {technique.shortDescription}
      </p>

      {/* Parameters card — duration / heat / temp at a glance. */}
      {hasParams && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 8,
          marginBottom: 22,
        }}>
          {duration && (
            <ParamPill label="DURATION" value={duration} tone={categoryColor} />
          )}
          {heat && (
            <ParamPill label="HEAT" value={heat} tone={categoryColor} />
          )}
          {temp && (
            <ParamPill label="TEMP" value={temp} tone={categoryColor} />
          )}
        </div>
      )}

      {/* Long description — the meat of the explainer. */}
      <p style={{
        fontFamily: FONT.forma, fontWeight: 400, fontSize: 15,
        lineHeight: 1.6, color: COLOR.ink, margin: "0 0 24px",
      }}>
        {technique.longDescription}
      </p>

      {/* Doneness cues — "Look for" section. Teal because progress /
          forward — the cook is checking they've reached the target. */}
      {technique.donenessCues.length > 0 && (
        <Section
          label="LOOK FOR"
          tone={COLOR.leaf}
          items={technique.donenessCues}
        />
      )}

      {/* Pitfalls — "Watch out for" section. Burnt-orange because
          warning per the progress/warning color rule. */}
      {technique.commonPitfalls.length > 0 && (
        <Section
          label="WATCH OUT FOR"
          tone={COLOR.rose}
          items={technique.commonPitfalls}
        />
      )}

      {/* Related techniques — tappable chips that swap the sheet
          content. Stays in-place; the parent caller doesn't need to
          re-open the sheet. */}
      {technique.relatedSlugs.length > 0 && (
        <div style={{ marginTop: 6, marginBottom: 8 }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            color: COLOR.muted, letterSpacing: "0.14em",
            marginBottom: 10,
          }}>
            RELATED
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {technique.relatedSlugs.map((relSlug) => {
              const rel = bySlug?.get(relSlug);
              if (!rel) return null;
              return (
                <button
                  key={relSlug}
                  onClick={() => setActiveSlug(relSlug)}
                  className="mcm-focusable"
                  style={{
                    appearance: "none",
                    fontFamily: FONT.forma, fontSize: 13, fontWeight: 500,
                    color: COLOR.ink,
                    background: COLOR.deep,
                    border: `1px solid ${COLOR.border}`,
                    borderRadius: RADIUS.pill,
                    padding: "6px 12px",
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  {rel.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </ModalSheet>
  );
}

function ParamPill({ label, value, tone }) {
  return (
    <div style={{
      display: "inline-flex", flexDirection: "column",
      padding: "8px 14px",
      background: COLOR.deep,
      border: `1px solid ${tone}40`,
      borderRadius: RADIUS.md,
      minWidth: 80,
    }}>
      <span style={{
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        color: tone, letterSpacing: "0.12em",
        marginBottom: 2,
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: FONT.forma, fontSize: 15, fontWeight: 600,
        color: COLOR.ink,
      }}>
        {value}
      </span>
    </div>
  );
}

function Section({ label, tone, items }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        color: tone, letterSpacing: "0.14em",
        marginBottom: 10,
      }}>
        {label}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {items.map((item, i) => (
          <li
            key={i}
            style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              padding: "6px 0",
              fontFamily: FONT.forma, fontSize: 14, lineHeight: 1.45,
              color: COLOR.ink, fontWeight: 400,
            }}
          >
            <span style={{
              flexShrink: 0,
              marginTop: 8,
              width: 4, height: 4,
              borderRadius: "50%", background: tone,
            }} />
            <span style={{ flex: 1 }}>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
